import React, { useState } from 'react';
import { theme } from '../../theme';
import { getApiUrl } from '../../config';
import { useResponsive } from '../../hooks/useResponsive';

export interface SeasonFormData {
  id: string;
  name: string;
  isActive: boolean;
  startDate: string;
  endDate?: string | null;
  pointsForWin?: number;
  pointsForDraw?: number;
  pointsForLoss?: number;
}

interface Props {
  groupId: string;
  mode: 'create' | 'edit';
  season?: SeasonFormData;
  onClose: () => void;
  onSaved: () => void;
}

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const toDateInput = (iso?: string | null): string => (iso ? iso.slice(0, 10) : '');

const SeasonFormModal: React.FC<Props> = ({ groupId, mode, season, onClose, onSaved }) => {
  const { isMobile } = useResponsive();
  const isEdit = mode === 'edit';

  const [name, setName] = useState(season?.name ?? '');
  const [startDate, setStartDate] = useState(toDateInput(season?.startDate) || toDateInput(new Date().toISOString()));
  const [endDate, setEndDate] = useState(toDateInput(season?.endDate));
  const [pointsForWin, setPointsForWin] = useState(String(season?.pointsForWin ?? 3));
  const [pointsForDraw, setPointsForDraw] = useState(String(season?.pointsForDraw ?? 1));
  const [pointsForLoss, setPointsForLoss] = useState(String(season?.pointsForLoss ?? 0));
  const [isActive, setIsActive] = useState(season?.isActive ?? true);
  const [includeExisting, setIncludeExisting] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Ingresá un nombre para la temporada');
      return;
    }
    if (!startDate) {
      setError('Ingresá una fecha de inicio');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        name: trimmed,
        startDate: new Date(startDate).toISOString(),
        endDate: endDate ? new Date(endDate).toISOString() : null,
        pointsForWin: Number(pointsForWin),
        pointsForDraw: Number(pointsForDraw),
        pointsForLoss: Number(pointsForLoss),
      };
      if (isEdit) body.isActive = isActive;
      else body.includeExisting = includeExisting;

      const url = isEdit
        ? `/api/groups/${groupId}/seasons/${season!.id}`
        : `/api/groups/${groupId}/seasons`;
      const res = await fetch(getApiUrl(url), {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        setError(json?.error || 'No se pudo guardar la temporada');
        return;
      }
      onSaved();
      onClose();
    } catch {
      setError('No se pudo guardar la temporada');
    } finally {
      setSubmitting(false);
    }
  };

  const sheetStyle: React.CSSProperties = {
    background: theme.colors.surface,
    borderRadius: isMobile ? '20px 20px 0 0' : 18,
    width: isMobile ? '100%' : 440,
    maxWidth: '100%',
    maxHeight: '92vh',
    overflowY: 'auto',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 13, fontWeight: 600, color: theme.colors.textSecondary, marginBottom: 6,
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '12px 14px', borderRadius: 12,
    border: `1.5px solid ${theme.colors.border}`, background: theme.colors.background,
    color: theme.colors.text, fontSize: 15, boxSizing: 'border-box', outline: 'none',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
        zIndex: 1002, padding: isMobile ? 0 : 16,
      }}
      onClick={() => !submitting && onClose()}
      onKeyDown={(e) => { if (e.key === 'Escape' && !submitting) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <form style={sheetStyle} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 2 }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: theme.colors.border }} />
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${theme.colors.border}` }}>
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            style={{ background: 'none', border: 'none', color: theme.colors.textSecondary, fontSize: 22, cursor: 'pointer' }}
            aria-label="Cerrar"
          >
            ✕
          </button>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: theme.colors.text }}>
            🏆 {isEdit ? 'Editar temporada' : 'Nueva temporada'}
          </h3>
          <div style={{ width: 24 }} />
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label htmlFor="season-name" style={labelStyle}>Nombre de la temporada</label>
            <input
              id="season-name" type="text" value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Apertura 2026, Temporada Marzo..."
              autoFocus maxLength={60} style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="season-start" style={labelStyle}>Fecha de inicio</label>
              <input id="season-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="season-end" style={labelStyle}>Fecha de fin (opcional)</label>
              <input id="season-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="pts-win" style={labelStyle}>Pts victoria</label>
              <input id="pts-win" type="number" value={pointsForWin} onChange={(e) => setPointsForWin(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="pts-draw" style={labelStyle}>Pts empate</label>
              <input id="pts-draw" type="number" value={pointsForDraw} onChange={(e) => setPointsForDraw(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="pts-loss" style={labelStyle}>Pts derrota</label>
              <input id="pts-loss" type="number" value={pointsForLoss} onChange={(e) => setPointsForLoss(e.target.value)} style={inputStyle} />
            </div>
          </div>

          {isEdit ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '12px 14px', borderRadius: 12, border: `1px solid ${theme.colors.border}`, background: theme.colors.background }}>
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} style={{ accentColor: theme.colors.primary, width: 18, height: 18, cursor: 'pointer' }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>Temporada activa</span>
            </label>
          ) : (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', padding: '12px 14px', borderRadius: 12, border: `1px solid ${theme.colors.border}`, background: theme.colors.background }}>
              <input type="checkbox" checked={includeExisting} onChange={(e) => setIncludeExisting(e.target.checked)} style={{ marginTop: 4, accentColor: theme.colors.primary, width: 18, height: 18, cursor: 'pointer' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, marginBottom: 2 }}>Incluir partidos anteriores</div>
                <div style={{ fontSize: 12, color: theme.colors.textSecondary, lineHeight: 1.4 }}>Suma los partidos ya jugados del grupo a la tabla (solo los que tienen resultado cargado).</div>
              </div>
            </label>
          )}

          {error && (
            <div style={{ fontSize: 13, color: theme.colors.danger, padding: '8px 12px', background: 'rgba(229,57,53,0.1)', borderRadius: 8 }}>
              {error}
            </div>
          )}

          <button
            type="submit" disabled={submitting}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px',
              borderRadius: 12, border: 'none', background: theme.colors.primary, color: theme.colors.background,
              fontSize: 15, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? 'Guardando...' : isEdit ? '💾 Guardar cambios' : '🏆 Crear temporada'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SeasonFormModal;
